import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { AuthStatus } from "../interfaces";
import { checkAuthAction, loginAction, registerAction } from "../actions";
import { syncI18nLocaleFromUser } from "@/i18n";
import { useLocalStorage } from "@vueuse/core";

export const useAuthStore = defineStore("auth", () => {
  const authStatus = ref(AuthStatus.Checking);
  const user = ref(undefined);
  const token = ref(useLocalStorage("vacio", ""));

  const login = async (email, password) => {
    try {
      const loginResp = await loginAction(email, password);
      if (!loginResp.ok) {
        logout();
        return false;
      }

      user.value = loginResp.user;
      token.value = loginResp.token;
      authStatus.value = AuthStatus.Authenticated;
      syncI18nLocaleFromUser(loginResp.user);

      return true;
    } catch (error) {
      return logout();
    }
  };

  const register = async (fullName, email, password) => {
    try {
      const registerResp = await registerAction(fullName, email, password);

      if (!registerResp.ok) {
        logout();
        return { ok: false, message: registerResp.message };
      }

      user.value = registerResp.user;
      token.value = registerResp.token;
      authStatus.value = AuthStatus.Authenticated;

      return { ok: true, message: "" };
    } catch (error) {
      return { ok: false, message: "Error al registrar el usuario" };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
    localStorage.removeItem("name");
    localStorage.removeItem("restaurant");
    localStorage.removeItem("stripeAccountId");

    localStorage.removeItem("customerName");
    localStorage.removeItem("customerAvatar");
    localStorage.removeItem("customerPhone");
    localStorage.removeItem("customerEmail");
    localStorage.removeItem("identifier");
    localStorage.removeItem("country");
    localStorage.removeItem("language");

    authStatus.value = AuthStatus.Unauthenticated;
    user.value = undefined;
    token.value = "";
    return false;
  };

  const checkAuthStatus = async () => {
    try {
      const statusResp = await checkAuthAction();

      if (!statusResp.ok) {
        logout();
        return false;
      }

      if (statusResp.transient) {
        token.value = statusResp.token || localStorage.getItem("token") || "";
        authStatus.value = AuthStatus.Authenticated;
        syncI18nLocaleFromUser(undefined);
        return true;
      }

      authStatus.value = AuthStatus.Authenticated;
      user.value = statusResp.user;
      token.value = statusResp.token;
      syncI18nLocaleFromUser(statusResp.user);
      return true;
    } catch (error) {
      const t = localStorage.getItem("token");
      if (t && t.length >= 10) {
        token.value = t;
        authStatus.value = AuthStatus.Authenticated;
        return true;
      }
      return false;
    }
  };

  return {
    user,
    token,
    authStatus,

    // Getters
    isChecking: computed(() => authStatus.value === AuthStatus.Checking),
    isAuthenticated: computed(
      () => authStatus.value === AuthStatus.Authenticated
    ),
    // Login guarda el nombre del rol en localStorage ("admin", etc.); el User de API usa `role` (ObjectId), no `roles[]`.
    isAdmin: computed(() => {
      if (typeof localStorage !== "undefined" && localStorage.getItem("role") === "admin") {
        return true;
      }
      const roles = user.value?.roles;
      return Array.isArray(roles) && roles.includes("admin");
    }),
    username: computed(() => user.value?.fullName),

    // Actions
    login,
    logout,
    register,
    checkAuthStatus,
  };
});
